import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useNavigate, Navigate, useLocation } from 'react-router-dom'
import { Button } from '../components/common/Button'
import { LoadingSpinner } from '../components/common/LoadingSpinner'
import { FlashcardReviewCard } from '../components/flashcards/FlashcardReviewCard'
import { ReviewStatistics } from '../components/flashcards/ReviewStatistics'
import { CongratulatoryModal } from '../components/common/CongratulatoryModal'
import { StreakCelebration } from '../components/common/StreakCelebration'
import { ConfirmModal } from '../components/common/Modal'
import { useUserStore } from '../store/userStore'
import { useLanguageStore } from '../store/languageStore'
import type { Flashcard, ReviewRating } from '../../../shared/types'
import api from '../services/api'
import { submitReviewDirectToFirestore } from '../services/firestoreFallback'
import { calculateNextReview, calculateNextReviewDate } from '../lib/srs'
import { prefetchFlashcardList } from '../store/flashcardCacheStore'
import { useScrollAnimation } from '../hooks/useScrollAnimation'

export function FlashcardReview() {
  const navigate = useNavigate()
  const location = useLocation()
  const user = useUserStore((state) => state.user)
  const currentLanguage = useLanguageStore((state) => state.currentLanguage)
  const availableLanguages = useLanguageStore((state) => state.availableLanguages)

  // Get review settings from navigation state
  const reviewDirection = (location.state as any)?.reviewDirection || 'mixed'
  const audioOnlyMode = (location.state as any)?.audioOnlyMode || false

  const [reviewQueue, setReviewQueue] = useState<Flashcard[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isFlipped, setIsFlipped] = useState(false)
  const [currentDirection, setCurrentDirection] = useState<'jp-en' | 'en-jp'>('jp-en')
  // Track which directions have been completed for each card (only in mixed mode)
  const [completedDirections, setCompletedDirections] = useState<Map<string, Set<'jp-en' | 'en-jp'>>>(new Map())
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [reviewedCount, setReviewedCount] = useState(0)
  // Track unique cards reviewed (by original card ID) for accurate progress in mixed mode
  const [reviewedUniqueCards, setReviewedUniqueCards] = useState<Set<string>>(new Set())
  const [repeatQueue, setRepeatQueue] = useState<Flashcard[]>([])
  const repeatQueueRef = useRef<Flashcard[]>([])
  useEffect(() => { repeatQueueRef.current = repeatQueue }, [repeatQueue])
  const [didReview, setDidReview] = useState(false)
  const [isCompletingReview, setIsCompletingReview] = useState(false)
  const [showCompletionModal, setShowCompletionModal] = useState(false)
  const [showEndSessionModal, setShowEndSessionModal] = useState(false)
  const [showStreakCelebration, setShowStreakCelebration] = useState(false)
  const [streakCelebrationData, setStreakCelebrationData] = useState({
    streakCount: 0,
    isNewStreak: false
  })
  const [completionModalData, setCompletionModalData] = useState({
    completedDailyGoal: false,
    totalReviewedToday: 0,
    dailyTarget: 0,
    uniqueCardsReviewed: 0
  })
  const [reviewProgress, setReviewProgress] = useState({
    reviewedToday: 0,
    totalDue: 0,
    percentageComplete: 0,
    remaining: 0,
  })
  const [startTime, setStartTime] = useState<number>(Date.now())
  const sessionStartTimeRef = useRef<number>(Date.now())
  const [dragX, setDragX] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)
  const [pendingRating, setPendingRating] = useState<ReviewRating | null>(null)
  const dragStartRef = useRef<number | null>(null)
  const finalizeTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const hasFinalized = useRef(false)
  const sessionFinishedRef = useRef(false)

  const targetLanguageLabel = useMemo(() => {
    const match = availableLanguages.find((lang) => lang.code === currentLanguage)
    return match?.name || (currentLanguage ? currentLanguage.toUpperCase() : 'JP')
  }, [availableLanguages, currentLanguage])

  const targetLanguageShort = useMemo(() => {
    return currentLanguage ? currentLanguage.toUpperCase() : 'JP'
  }, [currentLanguage])

  const warmFlashcardListCache = useCallback(() => {
    if (!user) return
    void prefetchFlashcardList({
      userId: user.id.toString(),
      targetLanguage: currentLanguage || undefined,
      masteryLevel: 'all',
    })
  }, [user, currentLanguage])

  const headerAnimation = useScrollAnimation<HTMLDivElement>({ threshold: 0.9 })
  const statsAnimation = useScrollAnimation<HTMLDivElement>({ threshold: 0.3 })
  const cardAnimation = useScrollAnimation<HTMLDivElement>({ threshold: 0.3 })
  const instructionsAnimation = useScrollAnimation<HTMLDivElement>({ threshold: 0.2, delay: 120 })

  const currentCard = reviewQueue[currentIndex]
  const remaining = reviewQueue.length - currentIndex - 1

  // For mixed mode, calculate display values
  const displayCardNumber = reviewDirection === 'mixed'
    ? Math.floor(currentIndex / 2) + 1
    : currentIndex + 1
  const displayTotalCards = reviewDirection === 'mixed'
    ? Math.ceil(reviewQueue.length / 2)
    : reviewQueue.length

  // Determine direction for the current card
  useEffect(() => {
    if (currentCard && (currentCard as any)._reviewDirection) {
      // Use the direction stored on the card (for mixed mode)
      setCurrentDirection((currentCard as any)._reviewDirection)
    } else if (reviewDirection === 'jp-en' || reviewDirection === 'en-jp') {
      setCurrentDirection(reviewDirection)
    } else {
      // Fallback for non-mixed mode
      setCurrentDirection('jp-en')
    }
  }, [currentCard, currentIndex, reviewDirection])

  useEffect(() => {
    loadReviewQueue()
    loadReviewProgress()
  }, [user])

  const loadReviewQueue = async () => {
    if (!user) return
    if (sessionFinishedRef.current) return

    setIsLoading(true)
    setError(null)

    try {
      // Always derive due locally from the full list to avoid any API timing/index issues
      const all = await api.getFlashcards(user.id.toString(), { limit: 1000, offset: 0 })
      const toLocalISO = (d: Date) => {
        const tz = d.getTimezoneOffset() * 60 * 1000
        return new Date(d.getTime() - tz).toISOString().split('T')[0]
      }
      const todayISO = toLocalISO(new Date())
      const isDue = (c: any) => {
        if (c.masteryLevel === 'new') return true
        const s = c.nextReviewDate
        if (!s) return false
        if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s <= todayISO
        try { return toLocalISO(new Date(s)) <= todayISO } catch { return false }
      }

      // Filter for audio mode: card is due if not reviewed in audio mode today
      const isDueForAudioMode = (c: any) => {
        // New cards are always due
        if (c.masteryLevel === 'new') return true

        // If card has never been reviewed in audio mode, it's due
        const audioDate = c.lastAudioReviewDate
        if (!audioDate) return true

        // Check if last audio review was before today
        if (/^\d{4}-\d{2}-\d{2}$/.test(audioDate)) return audioDate < todayISO
        try { return toLocalISO(new Date(audioDate)) < todayISO } catch { return true }
      }

      // Determine how many unique cards to fetch based on daily target
      // In mixed mode, each card is reviewed twice (both directions), so we fetch half the target
      const dailyTarget = user.dailyReviewTarget || 20
      const uniqueCardLimit = reviewDirection === 'mixed' ? Math.ceil(dailyTarget / 2) : dailyTarget

      // Use appropriate filter based on audio mode
      const filterFunc = audioOnlyMode ? isDueForAudioMode : isDue

      let queue = (all.flashcards as Flashcard[])
        .filter((c) => c.targetLanguage === currentLanguage) // Filter by current language
        .filter(filterFunc)
        .sort((a, b) => (a.masteryLevel === 'new' && b.masteryLevel !== 'new' ? -1 : a.nextReviewDate < b.nextReviewDate ? -1 : 1))
        .slice(0, uniqueCardLimit)

      // In mixed mode, duplicate each card for both directions
      if (reviewDirection === 'mixed') {
        const duplicatedQueue: any[] = []
        queue.forEach((card) => {
          // Add JP→EN version
          duplicatedQueue.push({
            ...card,
            _reviewDirection: 'jp-en' as const,
            _originalCardId: card.id || (card as any)._docId
          })
          // Add EN→JP version
          duplicatedQueue.push({
            ...card,
            _reviewDirection: 'en-jp' as const,
            _originalCardId: card.id || (card as any)._docId
          })
        })
        // Shuffle to alternate directions
        const shuffled: any[] = []
        for (let i = 0; i < queue.length; i++) {
          shuffled.push(duplicatedQueue[i * 2]) // JP→EN
          shuffled.push(duplicatedQueue[i * 2 + 1]) // EN→JP
        }
        setReviewQueue(shuffled)
      } else {
        setReviewQueue(queue)
      }

      if (queue.length === 0) setError('No cards due for review')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load review queue')
    } finally {
      setIsLoading(false)
    }
  }

  const loadReviewProgress = async () => {
    if (!user) return

    try {
      const data = await api.getReviewProgressAPI(user.id.toString(), user.dailyReviewTarget)
      setReviewProgress(data)
    } catch (err) {
      console.error('Failed to load review progress:', err)
    }
  }

  const handleFlip = () => {
    setIsFlipped((prev) => !prev)
  }

  const advanceToNextCard = async () => {
    console.log('advanceToNextCard called', { currentIndex, queueLength: reviewQueue.length })
    setReviewedCount((n) => n + 1)

    // Track unique cards reviewed (for mixed mode)
    if (currentCard) {
      const cardId = (currentCard as any)._originalCardId || currentCard.id || (currentCard as any)._docId
      setReviewedUniqueCards((prev) => new Set(prev).add(String(cardId)))
    }

    if (currentIndex < reviewQueue.length - 1) {
      console.log('Advancing to next card')
      setCurrentIndex((idx) => idx + 1)
      setIsFlipped(false)
      setStartTime(Date.now())
    } else {
      // Finished this pass; if we have missed cards, loop back over them
      if (repeatQueueRef.current.length > 0) {
        console.log('Pass complete. Waiting for pending submissions before re-queue', { count: repeatQueue.length })
        const timeoutAt = Date.now() + 2500
        while (pendingSubmitRef.current > 0 && Date.now() < timeoutAt) {
          await new Promise((r) => setTimeout(r, 120))
        }
        console.log('Re-queuing missed cards now')
        setReviewQueue(repeatQueueRef.current)
        repeatQueueRef.current = []
        setRepeatQueue([])
        setCurrentIndex(0)
        setIsFlipped(false)
        setStartTime(Date.now())
      } else {
        console.log('Review complete, showing summary')
        if (!isCompletingReview) {
          setIsCompletingReview(true)
          void handleReviewComplete()
        }
      }
    }
  }

  const handleRating = async (rating: ReviewRating, options?: { skipAdvance?: boolean }) => {
    console.log('handleRating called', { rating, options, hasCurrentCard: !!currentCard, hasUser: !!user })

    if (!currentCard || !user) {
      console.error('handleRating early return - missing currentCard or user', { currentCard, user })
      return
    }

    // Mark that at least one review occurred during this session
    setDidReview(true)

    const cardId = (currentCard as any)._originalCardId || currentCard.id || (currentCard as any)._docId
    const direction = (currentCard as any)._reviewDirection || currentDirection

    // In mixed mode, track completion by direction
    let shouldSubmitToBackend = true
    if (reviewDirection === 'mixed' && rating !== 'again') {
      const key = String(cardId)
      const completed = completedDirections.get(key) || new Set()
      completed.add(direction)

      // Only submit to backend if BOTH directions are now complete
      shouldSubmitToBackend = completed.size === 2

      setCompletedDirections(new Map(completedDirections.set(key, completed)))
    }

    // Check if card has a valid numeric ID
    // Firestore-generated IDs are typically 13+ digits (timestamps in milliseconds)
    // Our sequential numeric IDs should be much smaller (< 1 billion = 10 digits)
    const MAX_VALID_ID = 999999999 // Must match backend MAX_VALID_ID
    const hasValidNumericId = !!currentCard.id && currentCard.id > 0 && currentCard.id <= MAX_VALID_ID
    const docId = (currentCard as any)._docId as string | undefined

    const timeToAnswerMs = Date.now() - startTime
    console.log('Review decision', {
      cardId: currentCard.id,
      direction,
      rating,
      shouldSubmitToBackend,
      isMixed: reviewDirection === 'mixed'
    })

    if (shouldSubmitToBackend) {
      let settled = false

      // Fallback timer: if API is too slow, save directly to Firestore
      const fallbackTimer = setTimeout(() => {
        if (settled) return
        console.log('[fallback] API timeout - saving directly to Firestore')
        if (docId) {
          submitReviewDirectToFirestore(docId, currentCard, rating, timeToAnswerMs)
            .then((updated) => {
              try { console.log('[fallback] Firestore direct review saved (timeout)') } catch { }
              setReviewQueue((q) => q.map((c) => {
                const sameNumeric = (c.id && updated.id && c.id === updated.id)
                const sameDoc = (c as any)?._docId && (updated as any)?._docId && (c as any)._docId === (updated as any)._docId
                return (sameNumeric || sameDoc) ? { ...c, ...updated } : c
              }))
              repeatQueueRef.current = repeatQueueRef.current.map((c) => {
                const sameNumeric = (c.id && updated.id && c.id === updated.id)
                const sameDoc = (c as any)?._docId && (updated as any)?._docId && (c as any)._docId === (updated as any)._docId
                return (sameNumeric || sameDoc) ? { ...c, ...updated } as any : c
              })
              setRepeatQueue(repeatQueueRef.current)
              loadReviewProgress()
            })
            .catch((e) => console.error('[fallback] Firestore direct review failed (timeout)', e))
        }
      }, 2500)

      // Optimistically update local card immediately using the same SM-2 rules
      try {
        const srs = calculateNextReview({
          rating,
          currentEaseFactor: currentCard.easeFactor,
          currentIntervalDays: currentCard.intervalDays,
          currentRepetitions: currentCard.repetitions,
          currentMasteryLevel: currentCard.masteryLevel,
        })
        const nextDate = calculateNextReviewDate(srs.intervalDays)
        const prevTotal = (currentCard as any).totalReviews ?? 0
        const prevCorrect = (currentCard as any).correctReviews ?? 0
        const isCorrect = rating !== 'again'
        const newTotal = prevTotal + 1
        const newCorrect = prevCorrect + (isCorrect ? 1 : 0)
        const accuracy = Math.round((newCorrect / newTotal) * 100)
        const updatedLocal: any = {
          ...currentCard,
          masteryLevel: srs.masteryLevel,
          easeFactor: srs.easeFactor,
          intervalDays: srs.intervalDays,
          repetitions: srs.repetitions,
          nextReviewDate: nextDate,
          lastReviewDate: new Date().toISOString().split('T')[0],
          totalReviews: newTotal,
          correctReviews: newCorrect,
          accuracy,
        }
        setReviewQueue((q) => q.map((c) => {
          const sameNumeric = (c.id && updatedLocal.id && c.id === updatedLocal.id)
          const sameDoc = (c as any)?._docId && (updatedLocal as any)?._docId && (c as any)._docId === (updatedLocal as any)._docId
          return (sameNumeric || sameDoc) ? updatedLocal : c
        }))
        repeatQueueRef.current = repeatQueueRef.current.map((c) => {
          const sameNumeric = (c.id && updatedLocal.id && c.id === updatedLocal.id)
          const sameDoc = (c as any)?._docId && (updatedLocal as any)?._docId && (c as any)._docId === (updatedLocal as any)._docId
          return (sameNumeric || sameDoc) ? updatedLocal as any : c
        })
        setRepeatQueue(repeatQueueRef.current)
      } catch (e) {
        console.warn('Local SRS update failed (non-fatal)', e)
      }

      // Submit to API
      const localDate = new Date().toLocaleDateString('en-CA')
      let submitPromise
      const reviewPayload = {
        userId: user.id,
        rating,
        timeToAnswerMs,
        localDate,
      }

      if (hasValidNumericId) {
        submitPromise = api.submitFlashcardReviewAPI(currentCard.id, reviewPayload)
      } else if (docId) {
        submitPromise = api.submitFlashcardReviewByDocIdAPI(docId, reviewPayload)
      } else {
        submitPromise = Promise.reject(new Error('No valid ID for flashcard review submission'))
      }

      pendingSubmitRef.current++

      submitPromise
        .then((res: any) => {
          settled = true
          clearTimeout(fallbackTimer)
          try { console.log('Review API response', res) } catch { }

          // Check for streak data in response
          const streakData = res?.streakData || res?.data?.streakData
          console.log('[STREAK DEBUG] API response:', { res, streakData })

          if (streakData?.dailyGoalCompleted && streakData.streakMaintained) {
            console.log('[STREAK DEBUG] Showing celebration!', streakData)
            // Show streak celebration!
            setStreakCelebrationData({
              streakCount: streakData.newStreak,
              isNewStreak: streakData.newStreak === 1
            })
            setShowStreakCelebration(true)

            // Update user store with new streak
            if (user) {
              const { updateUser } = useUserStore.getState()
              updateUser({ studyStreak: streakData.newStreak })
            }
          } else {
            console.log('[STREAK DEBUG] Not showing celebration. Conditions:', {
              hasStreakData: !!streakData,
              dailyGoalCompleted: streakData?.dailyGoalCompleted,
              streakMaintained: streakData?.streakMaintained
            })
          }

          // Optimistically sync local queues with server-updated card
          const updated: any = res?.flashcard || res?.data?.flashcard || null
          if (updated) {
            setReviewQueue((q) => q.map((c) => {
              const sameNumeric = (c.id && updated.id && c.id === updated.id)
              const sameDoc = (c as any)?._docId && (updated as any)?._docId && (c as any)._docId === (updated as any)._docId
              return (sameNumeric || sameDoc) ? { ...c, ...updated } : c
            }))
            // Keep repeat queue in sync too
            repeatQueueRef.current = repeatQueueRef.current.map((c) => {
              const sameNumeric = (c.id && updated.id && c.id === updated.id)
              const sameDoc = (c as any)?._docId && (updated as any)?._docId && (c as any)._docId === (updated as any)._docId
              return (sameNumeric || sameDoc) ? { ...c, ...updated } as any : c
            })
            setRepeatQueue(repeatQueueRef.current)
          }
          loadReviewProgress()
        })
        .catch((err) => {
          settled = true
          clearTimeout(fallbackTimer)
          console.error('Failed to submit review:', err)
          // Fallback: write directly to Firestore from client
          if (docId) {
            submitReviewDirectToFirestore(docId, currentCard, rating, timeToAnswerMs)
              .then((updated) => {
                try { console.log('[fallback] Firestore direct review saved') } catch { }
                setReviewQueue((q) => q.map((c) => {
                  const sameNumeric = (c.id && updated.id && c.id === updated.id)
                  const sameDoc = (c as any)?._docId && (updated as any)?._docId && (c as any)._docId === (updated as any)._docId
                  return (sameNumeric || sameDoc) ? { ...c, ...updated } : c
                }))
                repeatQueueRef.current = repeatQueueRef.current.map((c) => {
                  const sameNumeric = (c.id && updated.id && c.id === updated.id)
                  const sameDoc = (c as any)?._docId && (updated as any)?._docId && (c as any)._docId === (updated as any)._docId
                  return (sameNumeric || sameDoc) ? { ...c, ...updated } as any : c
                })
                setRepeatQueue(repeatQueueRef.current)
                loadReviewProgress()
              })
              .catch((e) => {
                console.error('[fallback] Firestore direct review failed', e)
              })
          }
        })
        .finally(() => { pendingSubmitRef.current = Math.max(0, pendingSubmitRef.current - 1) })
    } else {
      console.log('Skipping backend submission - waiting for other direction in mixed mode')
    }

    // If user got it wrong, add to repeat queue for another pass later
    if (rating === 'again') {
      repeatQueueRef.current = [...repeatQueueRef.current, currentCard]
      setRepeatQueue(repeatQueueRef.current)
    }

    if (!options?.skipAdvance) {
      console.log('skipAdvance is false, calling advanceToNextCard')
      void advanceToNextCard()
    } else {
      console.log('skipAdvance is true, not calling advanceToNextCard')
    }
  }

  // Track pending review submissions to avoid navigating before updates are saved
  const pendingSubmitRef = useRef(0)

  // Safety net: after finishing all passes, navigate back to list
  // Only trigger if we're not showing completion modal
  useEffect(() => {
    if (didReview && reviewQueue.length === 0 && repeatQueue.length === 0 && !isAnimating && !isDragging && !showCompletionModal && !isCompletingReview) {
      navigate('/flashcards', { replace: true })
    }
  }, [didReview, reviewQueue.length, repeatQueue.length, isAnimating, isDragging, showCompletionModal, isCompletingReview, navigate])

  // Pointer (mouse/touch) swipe handlers
  const onPointerDown = (e: React.PointerEvent) => {
    if (!currentCard) return
    if (isAnimating) return
    // Don't set isDragging yet - wait for actual movement
    try { (e.currentTarget as any).setPointerCapture?.((e as any).pointerId) } catch { }
    dragStartRef.current = e.clientX
  }
  const onPointerMove = (e: React.PointerEvent) => {
    if (dragStartRef.current === null || isAnimating) return
    const dx = e.clientX - dragStartRef.current
    // Only start dragging if moved more than 5px
    if (Math.abs(dx) > 5 && !isDragging) {
      setIsDragging(true)
    }
    if (isDragging) {
      setDragX(dx)
    }
  }
  const finalizeSwipe = async () => {
    console.log('finalizeSwipe called', { pendingRating, hasFinalized: hasFinalized.current })

    // Prevent duplicate calls
    if (!pendingRating || hasFinalized.current) {
      console.log('Skipping finalize - already finalized or no pending rating')
      return
    }
    hasFinalized.current = true

    // Clear any pending timeout
    if (finalizeTimeoutRef.current) {
      clearTimeout(finalizeTimeoutRef.current)
      finalizeTimeoutRef.current = null
    }

    const rating = pendingRating
    console.log('Processing rating:', rating)

    try {
      // Do not await; let submission run in background
      void handleRating(rating, { skipAdvance: true })
      console.log('Rating submission dispatched')
    } catch (e) {
      console.error('Review submit dispatch failed (continuing)', e)
    }

    // Reset states BEFORE advancing to ensure clean state
    console.log('Resetting animation states')
    setPendingRating(null)
    setIsAnimating(false)
    setDragX(0)
    hasFinalized.current = false

    // Now advance to next card
    console.log('Calling advanceToNextCard')
    void advanceToNextCard()
  }

  const onPointerUp = async (e?: React.PointerEvent) => {
    const wasDragging = isDragging
    const dx = dragX

    // Reset drag state
    setIsDragging(false)
    dragStartRef.current = null
    try { (e?.currentTarget as any)?.releasePointerCapture?.((e as any).pointerId) } catch { }

    // If we never started dragging, treat it as a click to flip the card
    if (!wasDragging) {
      handleFlip()
      return
    }

    // Handle swipe gestures
    const threshold = 40
    const offscreen = Math.ceil(window.innerWidth * 1.25)
    if (dx <= -threshold) {
      setIsAnimating(true)
      setPendingRating('again')
      setDragX(-offscreen)
      // Fallback in case transitionend doesn't fire
      finalizeTimeoutRef.current = setTimeout(() => finalizeSwipe(), 600)
    } else if (dx >= threshold) {
      setIsAnimating(true)
      setPendingRating('good')
      setDragX(offscreen)
      finalizeTimeoutRef.current = setTimeout(() => finalizeSwipe(), 600)
    } else {
      // Not far enough: snap back
      setDragX(0)
    }
  }

  const onCardTransitionEnd = async (e: React.TransitionEvent<HTMLDivElement>) => {
    if (!isAnimating || !pendingRating) return
    if (e.propertyName !== 'transform') return
    // Finalize on actual transform transition end
    await finalizeSwipe()
  }

  // Keyboard shortcuts for quick rating (Left/Right) with animation like swipe
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!currentCard || isAnimating) return
      const offscreen = Math.ceil(window.innerWidth * 1.25)
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        setIsAnimating(true)
        setPendingRating('again')
        setDragX(-offscreen)
        finalizeTimeoutRef.current = setTimeout(() => finalizeSwipe(), 600)
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        setIsAnimating(true)
        setPendingRating('good')
        setDragX(offscreen)
        finalizeTimeoutRef.current = setTimeout(() => finalizeSwipe(), 600)
      } else if (e.key === ' ') {
        e.preventDefault()
        setIsFlipped((v) => !v)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [currentCard, isAnimating])

  // Track elapsed session time with optional awaiting
  const trackReviewTime = async () => {
    if (!user || reviewedUniqueCards.size === 0) return

    const reviewTimeSeconds = Math.floor((Date.now() - sessionStartTimeRef.current) / 1000)
    try {
      await api.addReviewTimeToUser(user.id.toString(), reviewTimeSeconds)
      console.log(`Tracked ${reviewTimeSeconds} seconds of review time`)
    } catch (err) {
      console.error('Failed to track review time:', err)
    }
  }

  // Navigate back only after pending submissions have flushed
  const navigateAfterPending = async () => {
    sessionFinishedRef.current = true

    // Track review time before navigating
    await trackReviewTime()
    warmFlashcardListCache()

    const timeoutAt = Date.now() + 1200
    const spin = () => {
      if (pendingSubmitRef.current === 0 || Date.now() >= timeoutAt) {
        navigate('/flashcards', { replace: true })
      } else {
        setTimeout(spin, 120)
      }
    }
    spin()
  }

  const handleReviewComplete = async () => {
    console.log('handleReviewComplete called')
    sessionFinishedRef.current = true

    // Check if user has completed their daily goal
    const totalReviewedToday = reviewProgress.reviewedToday + reviewedUniqueCards.size
    const dailyTarget = user?.dailyReviewTarget || 20
    const completedDailyGoal = totalReviewedToday >= dailyTarget

    console.log('Review stats:', { totalReviewedToday, dailyTarget, completedDailyGoal })

    // Track review time before showing completion (don't block UI)
    void trackReviewTime()
    warmFlashcardListCache()

    // Wait for any pending submissions to complete
    console.log('Waiting for pending submissions:', pendingSubmitRef.current)
    const timeoutAt = Date.now() + 1200
    while (pendingSubmitRef.current > 0 && Date.now() < timeoutAt) {
      await new Promise((r) => setTimeout(r, 120))
    }
    console.log('Pending submissions complete')

    // Show modal with completion data
    setCompletionModalData({
      completedDailyGoal,
      totalReviewedToday,
      dailyTarget,
      uniqueCardsReviewed: reviewedUniqueCards.size
    })
    setShowCompletionModal(true)
  }

  const handleModalClose = () => {
    setShowCompletionModal(false)
    navigate('/flashcards')
  }

  const handleEndSession = () => {
    setShowEndSessionModal(true)
  }

  // If no user, redirect immediately to profile to avoid loading spinner
  if (!user) {
    return <Navigate to="/profile" replace />
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <div className="mt-3 text-sm text-gray-500">Loading review queue...</div>
        </div>
      </div>
    )
  }

  if (error && reviewQueue.length === 0) {
    return (
      <div className="page-shell flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="text-6xl mb-4">🎉</div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">All caught up!</h1>
          <p className="text-gray-600 mb-6">
            No flashcards are due for review right now. Check back later or create more
            flashcards by practicing conversations.
          </p>
          <div className="flex space-x-3 justify-center">
            <Button onClick={() => navigate('/conversation')} variant="primary">
              Start Conversation
            </Button>
            <Button onClick={() => navigate('/flashcards')} variant="outline">
              View All Cards
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <StreakCelebration
        isOpen={showStreakCelebration}
        onClose={() => setShowStreakCelebration(false)}
        streakCount={streakCelebrationData.streakCount}
        isNewStreak={streakCelebrationData.isNewStreak}
      />
      <CongratulatoryModal
        isOpen={showCompletionModal}
        onClose={handleModalClose}
        completedDailyGoal={completionModalData.completedDailyGoal}
        totalReviewedToday={completionModalData.totalReviewedToday}
        dailyTarget={completionModalData.dailyTarget}
        uniqueCardsReviewed={completionModalData.uniqueCardsReviewed}
      />
      <ConfirmModal
        isOpen={showEndSessionModal}
        onClose={() => setShowEndSessionModal(false)}
        onConfirm={() => {
          void navigateAfterPending()
        }}
        title="End Review Session?"
        message={`You've reviewed ${reviewedUniqueCards.size} unique cards this session. End session now?`}
        confirmText="End Session"
        cancelText="Keep Reviewing"
      />

      <div className="page-shell">
        {/* Header */}
        <div
          ref={headerAnimation.ref}
          className={`bg-white border-b border-gray-200 px-4 py-4 ${headerAnimation.animationClasses}`}
        >
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900">Flashcard Review</h1>
                {audioOnlyMode && (
                  <span className="px-2 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full flex items-center gap-1">
                    🔊 Audio Only
                  </span>
                )}
                <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-full">
                  {reviewDirection === 'mixed'
                    ? `${targetLanguageShort} ⇄ EN`
                    : reviewDirection === 'jp-en'
                      ? `${targetLanguageShort} → EN`
                      : `EN → ${targetLanguageShort}`}
                </span>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                {reviewDirection === 'mixed' ? (
                  <>Card {displayCardNumber} of {displayTotalCards} ({currentDirection === 'jp-en' ? `${targetLanguageShort}→EN` : `EN→${targetLanguageShort}`}) • {remaining} remaining</>
                ) : (
                  <>Card {currentIndex + 1} of {reviewQueue.length} • {remaining} remaining</>
                )}
              </p>
            </div>
            <Button variant="outline" onClick={handleEndSession}>
              End Session
            </Button>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 py-8">

          {/* Review Statistics */}
          <div
            ref={statsAnimation.ref}
            className={`mb-8 ${statsAnimation.animationClasses}`}
          >
            <ReviewStatistics
              reviewedToday={reviewProgress.reviewedToday + reviewedUniqueCards.size}
              totalDue={reviewProgress.totalDue}
              dailyTarget={user?.dailyReviewTarget || 20}
              percentageComplete={reviewProgress.percentageComplete}
            />
          </div>

          {/* Review Card with swipe */}
          {currentCard && (
            <div
              ref={cardAnimation.ref}
              className={`mb-8 select-none ${cardAnimation.animationClasses}`}
            >
              <div key={(currentCard.id && currentCard.id > 0 && currentCard.id <= 1000000000) ? currentCard.id : (currentCard as any)._docId}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                onTransitionEnd={onCardTransitionEnd}
                onClick={(e) => {
                  // Prevent the card's onClick from firing since we handle it in onPointerUp
                  e.stopPropagation()
                }}
                style={{
                  transform: `translateX(${dragX}px) rotate(${dragX / 40}deg)`,
                  transition: isDragging ? 'none' : 'transform 220ms ease',
                  pointerEvents: isAnimating ? 'none' : 'auto',
                  touchAction: 'pan-y', // Allow vertical scroll but keep horizontal swipes responsive
                  willChange: 'transform',
                }}
              >
                <FlashcardReviewCard
                  flashcard={currentCard}
                  isFlipped={isFlipped}
                  onFlip={handleFlip}
                  autoPlayOnMount
                  direction={currentDirection}
                  audioOnlyMode={audioOnlyMode}
                  usePremiumEnglishAudio={user?.subscriptionTier === 'premium'}
                  preferredVoiceId={user?.preferredVoiceId ?? null}
                />
              </div>
              <div className="mt-3 text-center text-sm text-gray-500">
                Swipe left = Didn’t get it • Swipe right = Got it
              </div>
            </div>
          )}

          {/* Simplified UX: no rating buttons, use swipe/keys only */}

          {/* Instructions */}
          {currentCard && (
            <div
              ref={instructionsAnimation.ref}
              className={`text-center text-sm text-gray-500 mt-8 ${instructionsAnimation.animationClasses}`}
            >
              <p>Swipe left = Didn’t get it • Swipe right = Got it (Arrow keys also work)</p>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default FlashcardReview


